import React, { useCallback, useEffect, useState } from "react";
import Sidebar, { SidebarProvider } from "../../components/navigation/Sidebar";
import { supabase } from "../../lib/supabase";
import { useLanguageProfile } from "../../contexts/LanguageProfileContext";
import { useModal } from "../../components/ui/ModalProvider";
import { useRequestGuard } from "../../hooks/useRequestGuard";

const FUNCTIONS_URL = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

async function getAccessToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

function InstagramIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
    </svg>
  );
}

function Spinner() {
  return <div className="w-7 h-7 border-2 border-gray-200 border-t-pink-500 rounded-full animate-spin" />;
}

function isExpired(account) {
  return Boolean(account.token_expires_at && new Date(account.token_expires_at) <= new Date());
}

function StatusBadge({ connected }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
      connected ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-green-500" : "bg-amber-500"}`} />
      {connected ? "Connected" : "Token Expired"}
    </span>
  );
}

export default function InstagramAccounts() {
  const { activeProfile } = useLanguageProfile();
  const { alert, confirm } = useModal();
  const startAccountsRequest = useRequestGuard();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState(null);

  const params = new URLSearchParams(window.location.search);
  const connected = params.get("connected");
  const oauthError = params.get("error");
  const errorDetail = params.get("detail");

  useEffect(() => {
    if (connected === "true") alert("✅ Instagram account connected successfully!");
    if (oauthError) {
      const detail = errorDetail ? `\n\nDetails: ${errorDetail}` : "";
      alert(`❌ Instagram connection failed: ${friendlyError(oauthError)}${detail}`);
    }
    if (connected || oauthError) window.history.replaceState({}, "", window.location.pathname);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    startAccountsRequest();
    setAccounts([]);
  }, [activeProfile?.id, startAccountsRequest]);

  const loadAccounts = useCallback(async () => {
    const isCurrent = startAccountsRequest();
    if (!activeProfile?.id) {
      setAccounts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("Authentication expired. Please sign in again.");
      const response = await fetch(
        `${FUNCTIONS_URL}/instagram-accounts?profileId=${encodeURIComponent(activeProfile.id)}`,
        { headers: { Authorization: `Bearer ${token}`, apikey: ANON_KEY } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Unable to load Instagram accounts");
      if (isCurrent()) setAccounts(data.accounts || []);
    } catch (error) {
      if (isCurrent()) {
        console.error("[InstagramAccounts] Load failed:", error);
        setAccounts([]);
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [activeProfile?.id, startAccountsRequest]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const handleConnect = async () => {
    if (!activeProfile?.id) {
      alert("Please select a Gymplix profile first.");
      return;
    }

    setConnecting(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("Authentication expired. Please sign in again.");
      const response = await fetch(
        `${FUNCTIONS_URL}/instagram-auth-url?profileId=${encodeURIComponent(activeProfile.id)}`,
        { headers: { Authorization: `Bearer ${token}`, apikey: ANON_KEY } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.url) throw new Error(data.error || "Unable to start Instagram Login");
      window.location.assign(data.url);
    } catch (error) {
      alert(`❌ Could not connect Instagram: ${error.message}`);
      setConnecting(false);
    }
  };

  const handleDisconnect = async (account) => {
    if (!(await confirm(`Disconnect @${account.username || "instagram"} from Gymplix?`))) return;
    setDisconnectingId(account.id);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("Authentication expired. Please sign in again.");
      const response = await fetch(`${FUNCTIONS_URL}/instagram-accounts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          apikey: ANON_KEY,
        },
        body: JSON.stringify({ action: "delete", accountId: account.id, profileId: activeProfile.id }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Disconnect failed");
      setAccounts((current) => current.filter((item) => item.id !== account.id));
    } catch (error) {
      alert(`❌ Failed to disconnect Instagram: ${error.message}`);
    } finally {
      setDisconnectingId(null);
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="main-content">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white flex items-center justify-center">
                  <InstagramIcon size={22} />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900">Instagram Accounts</h1>
                  <p className="text-xs text-gray-500">Instagram API connections for the selected Gymplix profile.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleConnect}
                disabled={connecting || !activeProfile?.id}
                className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-500 text-white text-sm font-semibold disabled:opacity-50"
              >
                {connecting ? "Connecting…" : "Connect Instagram"}
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-20"><Spinner /></div>
            ) : accounts.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-pink-50 text-pink-600 mx-auto flex items-center justify-center">
                  <InstagramIcon size={32} />
                </div>
                <h2 className="mt-4 text-lg font-semibold text-gray-900">No Instagram Account Connected</h2>
                <p className="mt-2 text-sm text-gray-500">Connect an Instagram professional account to this Gymplix profile.</p>
                <button
                  type="button"
                  onClick={handleConnect}
                  disabled={connecting || !activeProfile?.id}
                  className="mt-6 px-6 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-500 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {connecting ? "Connecting…" : "Connect Instagram"}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {accounts.map((account) => (
                  <div key={account.id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white flex items-center justify-center flex-shrink-0">
                        <InstagramIcon size={24} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900">@{account.username || "Instagram account"}</h3>
                          <StatusBadge connected={!isExpired(account)} />
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Connected {new Date(account.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDisconnect(account)}
                        disabled={disconnectingId === account.id}
                        className="px-3 py-1.5 rounded-md border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        {disconnectingId === account.id ? "Disconnecting…" : "Disconnect"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}

function friendlyError(code) {
  const errors = {
    cancelled: "You cancelled Instagram Login.",
    invalid_state: "The Instagram Login session expired. Please try again.",
    missing_params: "Instagram returned an incomplete authorization response.",
    token_exchange: "Instagram could not authorize this account.",
    profile_fetch: "Instagram authorized the account but did not return its profile.",
    db_save: "Gymplix could not save the Instagram account.",
    config: "Instagram OAuth is not configured.",
    server_error: "An unexpected connection error occurred.",
  };
  return errors[code] || code;
}
